import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
    Button,
    EmptyState,
    EmptyStateActions,
    EmptyStateContent,
    EmptyStateDescription,
    EmptyStateIcon,
    EmptyStateTitle,
} from '@/ui/design-system/components';
import {
    useAlertConfigs,
    useDeleteAlertConfig,
    useUpdateAlertConfig,
} from 'utils/hooks/alerts';
import { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import AlertConfigForm from './Alerts/AlertConfigForm';
import AlertPreview from './AlertPreview';
import { parseReceiverEmails } from './Alerts/receiverEmails';
import { FiPlus } from 'react-icons/fi';
import Link from 'components/Link';
import Loading from 'components/Loading';
import { SidebarTrigger } from '@/ui/design-system/components/ui/sidebar';
import { mixpanelTrack } from 'utils/app';
import toast from 'react-hot-toast';
import { useQueryClient } from 'react-query';

function AlertConfigWrapper({ children }) {
    return (
        <div className="grid grid-cols-1 md:grid-cols-[25rem_1fr] md:h-[calc(100svh)] md:max-h-[calc(100svh)] md:overflow-hidden">
            {children}
        </div>
    );
}

function AlertEdit() {
    const qc = useQueryClient();
    const history = useHistory();
    const { alert_config_id } = useParams();
    const configsQuery = useAlertConfigs();
    const updateConfig = useUpdateAlertConfig();
    const deleteConfig = useDeleteAlertConfig();
    const configs = configsQuery.data;
    const config = configs?.alert_configs?.find(
        (c) => c.alert_config_id === alert_config_id,
    );
    const [previewConfig, setPreviewConfig] = useState(null);

    useEffect(() => {
        setPreviewConfig(config || null);
    }, [config]);

    useEffect(() => {
        if (config) {
            mixpanelTrack('Alert Config View', {
                alertConfigId: alert_config_id,
                brandName: config.brand_name,
                categories: config.categories
                    ?.map((cat) => cat.cat3 || cat.cat2)
                    .filter(Boolean),
                country: config.country,
                trendType: config.trend_type,
                dataSource: config.data_source,
            });
        }
    }, [alert_config_id, config]);

    const handleUpdate = async (formData) => {
        try {
            await updateConfig.mutateAsync({
                alertConfigId: alert_config_id,
                data: formData,
            });
            qc.invalidateQueries('alertConfigs');

            mixpanelTrack('Update Alert', {
                alertConfigId: alert_config_id,
                numberOfRecipients: parseReceiverEmails(
                    formData.receiver_emails,
                ).length,
                categories: formData.categories
                    ?.map((cat) => cat.cat3 || cat.cat2)
                    .filter(Boolean),
                country: formData.country,
                trendType: formData.trend_type,
                name: formData.name,
                brandName: formData.brand_name,
                brandWebsite: formData.website_url,
                dataSource: formData.data_source,
            });

            toast.success('Update saved!');
        } catch (error) {
            console.error('Failed to update alert:', error);
            toast.error('Update failed. Please try again.');
        }
    };

    const handleDelete = async () => {
        try {
            mixpanelTrack('Delete Alert', {
                alertConfigId: alert_config_id,
                brandName: config?.brand_name,
                categories: config?.categories
                    ?.map((cat) => cat.cat3 || cat.cat2)
                    .filter(Boolean),
                country: config?.country,
                trendType: config?.trend_type,
                dataSource: config?.data_source,
            });

            await deleteConfig.mutateAsync(alert_config_id);
            qc.invalidateQueries('alertConfigs');
            toast.success('Alert deleted');
            history.push('/alerts');
        } catch (error) {
            console.error('Failed to delete alert:', error);
            toast.error('Delete failed. Please try again.');
        }
    };
    if (configsQuery.isLoading) {
        return (
            <AlertConfigWrapper>
                <div className="flex justify-center items-center h-32">
                    <Loading />
                </div>
            </AlertConfigWrapper>
        );
    }

    if (!config && configsQuery.status === 'success') {
        return (
            <div>
                <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200">
                    <SidebarTrigger className="md:hidden" />
                    <Breadcrumb>
                        <BreadcrumbList>
                            <BreadcrumbItem>
                                <BreadcrumbLink asChild>
                                    <Link to="/alerts">Alerts</Link>
                                </BreadcrumbLink>
                            </BreadcrumbItem>
                            <BreadcrumbSeparator />
                            <BreadcrumbItem>
                                <BreadcrumbPage>Not found</BreadcrumbPage>
                            </BreadcrumbItem>
                        </BreadcrumbList>
                    </Breadcrumb>
                    <Button className="ml-auto" asChild>
                        <Link to="/alerts/new">
                            <FiPlus className="mr-2 h-4 w-4" />
                            Create new alert
                        </Link>
                    </Button>
                </div>
                <div className="p-5">
                    <EmptyState>
                        <EmptyStateContent>
                            <EmptyStateIcon />
                            <div className="flex flex-col gap-2">
                                <EmptyStateTitle>
                                    Alert not found
                                </EmptyStateTitle>
                                <EmptyStateDescription>
                                    The alert you're looking for doesn't exist
                                    or has been deleted.
                                </EmptyStateDescription>
                            </div>
                            <EmptyStateActions>
                                <Button asChild>
                                    <Link to="/alerts/new">
                                        <FiPlus className="mr-2 h-4 w-4" />
                                        Create new alert
                                    </Link>
                                </Button>
                                <Button variant="outline" asChild>
                                    <Link to="/alerts">View all alerts</Link>
                                </Button>
                            </EmptyStateActions>
                        </EmptyStateContent>
                    </EmptyState>
                </div>
            </div>
        );
    }

    return (
        <AlertConfigWrapper>
            <AlertConfigForm
                config={config}
                onSubmit={handleUpdate}
                onDelete={handleDelete}
                isSubmitting={updateConfig.isLoading}
                isDeleting={deleteConfig.isLoading}
                onConfigChange={setPreviewConfig}
                key={`${config?.alert_config_id}-${config?.updated_at}`}
            />
            <AlertPreview
                alertConfigId={alert_config_id}
                updatedAt={config?.updated_at}
                config={previewConfig || config}
            />
        </AlertConfigWrapper>
    );
}

export default AlertEdit;
